'use client';

/**
 * The 11 quiz step components, consolidated. Each is a small pure
 * function of `(profile, leadForm, onChange)` — no data fetching, no
 * DB; the parent StartFlowClient owns state + the upsert API calls.
 *
 * Step components stay deliberately compact (one file) since they
 * each have only the chrome that's specific to their question shape.
 * Common chrome (heading, layout, primary button) lives in StepShell.
 */

import { useState } from 'react';
import {
  Activity,
  BarChart3,
  Brain,
  Check,
  CheckCircle2,
  CircleDashed,
  CircleSlash,
  Compass,
  Droplet,
  Droplets,
  Flame,
  Gauge,
  Heart,
  HeartPulse,
  HelpCircle,
  Microscope,
  Ribbon,
  ShieldAlert,
  Sparkles,
  TimerReset,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { OptionCard, FormField, StepShell } from './StartUI';
import { LegalLink } from '@/components/LegalLink';
import { CONCERN_META } from '@/app/dashboard/bloodwork/concern-meta';
import {
  applyOptionOverrideSerialized,
  EMPTY_SERIALIZED_QUIZ_OVERRIDES,
  type SerializedQuizOverrides,
} from '@/lib/quiz/types';
import {
  PRIMARY_CONCERNS,
  ACTIVITY_LEVELS,
  DIET_PATTERNS,
  ENERGY_PATTERNS,
  COGNITIVE_STATES,
  MEMORY_STATES,
  SLEEP_HOURS_BANDS,
  DIAGNOSED_CONDITIONS,
  FAMILY_HISTORY_CATEGORIES,
  PRIMARY_GOALS,
  MENSTRUAL_STATUSES,
  CYCLE_PHASES,
  BIRTH_CONTROL_TYPES,
  type HealthProfileInput,
  type PrimaryConcern,
  type DiagnosedCondition,
  type FamilyHistoryCategory,
  type ActivityLevel,
  type DietPattern,
  type EnergyPattern,
  type CognitiveState,
  type MemoryState,
  type SleepHoursBand,
  type PrimaryGoal,
  type MenstrualStatus,
  type CyclePhase,
  type BirthControlType,
} from '@/lib/health-profile/types';

export interface LeadForm {
  firstName: string;
  lastName: string;
  email: string;
  // Track 5.Y2-start-quiz-polish (2026-05-12) — phone collection moved
  // BACK to /start with TCPA-compliant explicit opt-in. Reverses the
  // 2026-04-30 §6.2 decision; the new TCPA gating (default-unchecked
  // checkbox + Reply-STOP language + linked /legal/sms-terms) is the
  // discipline that wasn't present in the pre-2026-04-30 pattern, so
  // the reversal is architectural, not regression. See HEARTBEAT §6.2
  // 2026-05-12 entry for the full record.
  phone: string;          // 10-digit US (digits only, formatted on display)
  smsConsent: boolean;    // TCPA opt-in — defaults false, requires explicit click
  dateOfBirth: string;
  sexAtBirth: 'female' | 'male' | '';
  zipCode: string;
}

interface ProfileChange {
  (patch: Partial<HealthProfileInput>): void;
}
interface LeadChange {
  (patch: Partial<LeadForm>): void;
}

// Track 5.Y-cat-quiz-admin Commit 1 — every step accepts overrides
// (default EMPTY_SERIALIZED_QUIZ_OVERRIDES) and threads through to
// <StepShell> via stepId + overrides props. <StepShell> internally
// resolves overrides for title + subtitle.
//
// Per-option label overrides wired in Track 5.Y-cat-quiz-composer-
// tightening — each <OptionCard label={...}> below wraps the hardcoded
// label in applyOptionOverrideSerialized. Mirrors the ClarityClient.tsx
// pattern. The option_value enums themselves stay code-locked (changing
// would break Zod, DB persistence, funnel intent filter, personalized-
// context); only the displayed label is overridable.
//
// Note: shared/quiz/spec.ts currently lists step.options for 6 of the
// 9 wired sites (concerns / energy / focus / memory / sleep_band / goal
// partially). conditions / family_history / lifestyle activity+diet
// have options:[] in spec, so the admin UI doesn't surface them for
// editing — wiring stays forward-compatible for when spec.options is
// extended. Renderer falls back to hardcoded labels when no override.
const NO_OVERRIDES: SerializedQuizOverrides = EMPTY_SERIALIZED_QUIZ_OVERRIDES;

// Display labels — keeps copy out of the enum constants.
const CONCERN_LABEL: Record<PrimaryConcern, { label: string; description?: string }> = {
  energy:            { label: 'Energy' },
  sleep:             { label: 'Sleep' },
  weight:            { label: 'Weight' },
  mood:              { label: 'Mood' },
  cognition:         { label: 'Focus & cognition' },
  digestion:         { label: 'Digestion' },
  hormones:          { label: 'Hormones' },
  heart:             { label: 'Heart health' },
  immune:            { label: 'Immune system' },
  skin:              { label: 'Skin' },
  inflammation:      { label: 'Inflammation' },
  longevity:         { label: 'Longevity' },
  wellness_baseline: { label: 'Just a baseline' },
};

const CONDITION_LABEL: Record<DiagnosedCondition, string> = {
  diabetes_t1:        'Type 1 diabetes',
  diabetes_t2:        'Type 2 diabetes',
  prediabetes:        'Prediabetes',
  hypothyroidism:     'Hypothyroidism',
  hyperthyroidism:    'Hyperthyroidism',
  pcos:               'PCOS',
  endometriosis:      'Endometriosis',
  celiac:             'Celiac disease',
  ibs:                'IBS',
  ibd:                'IBD (Crohn’s / colitis)',
  hypertension:       'High blood pressure',
  high_cholesterol:   'High cholesterol',
  autoimmune_other:   'Other autoimmune',
  cancer_history:     'Cancer history',
  kidney_disease:     'Kidney disease',
  liver_disease:      'Liver disease',
  heart_disease:      'Heart disease',
  anemia:             'Anemia',
  none:               'None of these',
};

const FAMILY_LABEL: Record<FamilyHistoryCategory, string> = {
  diabetes:      'Diabetes',
  heart_disease: 'Heart disease',
  cancer:        'Cancer',
  thyroid:       'Thyroid disease',
  autoimmune:    'Autoimmune',
  mental_health: 'Mental health',
  none:          'None',
  unknown:       'Not sure',
};

const ACTIVITY_LABEL: Record<ActivityLevel, { label: string; description: string }> = {
  sedentary: { label: 'Sedentary',  description: 'Mostly sitting, little exercise' },
  light:     { label: 'Light',       description: '1–2 light workouts a week' },
  moderate:  { label: 'Moderate',    description: '3–4 workouts a week' },
  athletic:  { label: 'Athletic',    description: '5+ workouts a week or competitive' },
};

const DIET_LABEL: Record<DietPattern, { label: string; description: string }> = {
  omnivore:      { label: 'Omnivore',      description: 'Most foods, mixed protein' },
  vegetarian:    { label: 'Vegetarian',    description: 'No meat, includes dairy/eggs' },
  vegan:         { label: 'Vegan',         description: 'Plant-based only' },
  pescatarian:   { label: 'Pescatarian',   description: 'Vegetarian plus fish' },
  keto:          { label: 'Keto',          description: 'Low-carb, high-fat' },
  mediterranean: { label: 'Mediterranean', description: 'Fish, olive oil, whole grains' },
  paleo:         { label: 'Paleo',         description: 'Whole foods, no grains/legumes' },
  other:         { label: 'Other',         description: 'Something else' },
};

// Phase 1 dimension labels — each card carries a short label + an
// honest descriptor. Voice rules: second-person, plain English, no
// clinical hedging. The descriptor is what the customer would say
// about themselves out loud, not a diagnostic euphemism.
const ENERGY_LABEL: Record<EnergyPattern, { label: string; description: string }> = {
  steady:           { label: 'Steady, all day',                description: "Energy holds from morning through evening." },
  morning_only:     { label: 'Solid mornings, fade after lunch', description: "Sharp until early afternoon, then it drops." },
  persistent_drag:  { label: 'Drag through most of it',         description: "Tired most of the day, even after sleeping." },
  afternoon_crash:  { label: 'Crash at 3 PM',                   description: "Reliable mid-afternoon dip every day." },
  wired_tired:      { label: 'Wired but tired',                 description: "Exhausted but can't fully wind down." },
};

const COGNITIVE_LABEL: Record<CognitiveState, { label: string; description: string }> = {
  cognitive_sharp: { label: 'Sharp focus for hours',                  description: "Hold complex thinking for an hour-plus without drifting." },
  solid_drift:     { label: 'Solid, occasional drift',                description: "Mostly clear; mind wanders sometimes during long tasks." },
  brain_fog:       { label: 'Brain fog most days',                    description: "Words don't come fast; tasks take longer than they should." },
  severe_fog:      { label: "Can't hold a thought for ten minutes",   description: "Concentration breaks before I finish what I started." },
};

const MEMORY_LABEL: Record<MemoryState, { label: string; description: string }> = {
  memory_sharp:         { label: 'Sharp — names, tasks, conversations', description: "Recall details without effort." },
  occasional_misplace:  { label: 'Misplace things sometimes',           description: "Normal stuff — keys, parking spots — not a real concern." },
  forget_names:         { label: 'Forget names a lot',                  description: "Faces I know but names blank, more than they used to." },
  worried_decline:      { label: 'Worried about decline',               description: "Notice memory slipping in a way that scares me." },
};

const SLEEP_BAND_LABEL: Record<SleepHoursBand, { label: string; description: string }> = {
  under_5: { label: 'Less than 5 hours', description: "Survival mode — body running on empty." },
  '5_to_6': { label: '5–6 hours',         description: "A bit short for what most adults actually need." },
  '6_to_7': { label: '6–7 hours',         description: "Common amount — not optimal for most adults." },
  '7_to_8': { label: '7–8 hours',         description: "Solid — you wake feeling rested most days." },
  '8_to_9': { label: '8–9 hours',         description: "Plenty — you wake fully restored." },
  over_9:  { label: 'More than 9 hours', description: "Long sleeper — or chasing recovery from something." },
};

const GOAL_LABEL: Record<PrimaryGoal, { label: string; description: string }> = {
  baseline_health:      { label: 'I want a baseline',      description: 'See where I stand right now' },
  optimize_performance: { label: 'Optimizing performance', description: 'Train and recover smarter' },
  investigate_symptom:  { label: 'Investigating symptoms', description: 'I have something I want to understand' },
  track_over_time:      { label: 'Tracking over time',     description: 'Build a trend, not a snapshot' },
  preventive_screening: { label: 'Preventive screening',   description: 'Catch what doctors might miss' },
  longevity:            { label: 'Longevity',              description: 'Add good years' },
  other:                { label: 'Something else',         description: 'I’ll explain in the goals notes later' },
};

const MENSTRUAL_LABEL: Record<MenstrualStatus, string> = {
  regular:        'Regular cycles',
  irregular:      'Irregular cycles',
  pregnant:       'Pregnant',
  breastfeeding:  'Breastfeeding',
  postmenopausal: 'Postmenopausal',
  perimenopausal: 'Perimenopausal',
  not_applicable: 'Prefer not to say',
};

const CYCLE_LABEL: Record<CyclePhase, string> = {
  menstrual:    'Menstrual (days 1–5)',
  follicular:   'Follicular (days 6–13)',
  ovulatory:    'Ovulatory (days 14–16)',
  luteal:       'Luteal (days 17+)',
  not_tracking: 'Not tracking',
};

// Per-option Lucide icons. Shared with Lab Results Symptom mode where
// possible (Q1 concerns reuses CONCERN_META), and a matching set for
// Q4 conditions + Q5 family history so every option card on those
// multi-select steps has a recognizable glyph in its circle.
const CONCERN_ICON: Record<PrimaryConcern, LucideIcon> = (() => {
  const byKey = new Map(CONCERN_META.map((m) => [m.key, m.icon]));
  const out = {} as Record<PrimaryConcern, LucideIcon>;
  for (const c of PRIMARY_CONCERNS) out[c] = byKey.get(c) ?? Sparkles;
  return out;
})();

const CONDITION_ICON: Record<DiagnosedCondition, LucideIcon> = {
  diabetes_t1:      Droplets,
  diabetes_t2:      Droplets,
  prediabetes:      Droplets,
  hypothyroidism:   Flame,
  hyperthyroidism:  Flame,
  pcos:             Sparkles,
  endometriosis:    Sparkles,
  celiac:           CircleSlash,
  ibs:              CircleDashed,
  ibd:              CircleDashed,
  hypertension:     Activity,
  high_cholesterol: HeartPulse,
  autoimmune_other: ShieldAlert,
  cancer_history:   Ribbon,
  kidney_disease:   Droplet,
  liver_disease:    Droplet,
  heart_disease:    Heart,
  anemia:           Droplet,
  none:             CheckCircle2,
};

const FAMILY_ICON: Record<FamilyHistoryCategory, LucideIcon> = {
  diabetes:      Droplets,
  heart_disease: Heart,
  cancer:        Ribbon,
  thyroid:       Flame,
  autoimmune:    ShieldAlert,
  mental_health: Brain,
  none:          CheckCircle2,
  unknown:       HelpCircle,
};

// Phase 2 (Lock 5f): Lucide icon mapping for the goal step's primary
// goals chips. Mirrors the CONCERN/CONDITION/FAMILY pattern so each
// option card carries a recognizable glyph in its 40×40 themed bubble.
const GOAL_ICON: Record<PrimaryGoal, LucideIcon> = {
  baseline_health:      Gauge,
  optimize_performance: TrendingUp,
  investigate_symptom:  Microscope,
  track_over_time:      BarChart3,
  preventive_screening: ShieldAlert,
  longevity:            TimerReset,
  other:                Compass,
};

const BIRTH_CONTROL_LABEL: Record<BirthControlType, string> = {
  none:         'None',
  oral:         'Oral pill',
  iud_hormonal: 'IUD (hormonal)',
  iud_copper:   'IUD (copper)',
  implant:      'Implant',
  shot:         'Shot',
  patch:        'Patch',
  ring:         'Ring',
  other:        'Other',
};

// ─────────────────────────────────────────────────────────────────────
// Step components
// ─────────────────────────────────────────────────────────────────────

// Track 5.Y2-start-quiz-polish: cap raised 3 → 6.
// Track 5.Y3-start-quiz-bugfix-polish (2026-05-12): "Just a baseline"
// card + "OR JUST EXPLORE" divider removed — designer-lead flagged the
// dual affordance as redundant. "Show me everything" is now the single
// comprehensive-funnel path (still produces the same renderer behavior
// as the baseline-empty case via the unification in derive-intents.ts).
// The wellness_baseline enum value stays in PRIMARY_CONCERNS for the
// concern-markers + funnel resolver, just not user-reachable from /start.
const CONCERNS_MAX = 6;
const NON_BASELINE_CONCERNS = PRIMARY_CONCERNS.filter((c) => c !== 'wellness_baseline');

export function ConcernsStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  const selected = profile.primaryConcerns ?? [];
  // "Show me everything" === every non-baseline concern is selected.
  const allSelected = NON_BASELINE_CONCERNS.every((c) => selected.includes(c));
  // Track 5.Y3: track when the bulk-select fires so the per-card
  // staggered animation gets a fresh `key` to remount + replay.
  const [bulkSelectAt, setBulkSelectAt] = useState(0);

  function toggle(c: PrimaryConcern) {
    const has = selected.includes(c);
    if (has) {
      onChange({ primaryConcerns: selected.filter((x) => x !== c) });
    } else if (selected.length < CONCERNS_MAX) {
      onChange({ primaryConcerns: [...selected, c] });
    }
  }

  function selectAll() {
    if (allSelected) {
      // Toggle off — clear the selection.
      onChange({ primaryConcerns: [] });
    } else {
      // Select all 12 non-baseline concerns. The staggered visual
      // feedback fires on remount via the per-card animation-delay
      // styles below.
      onChange({ primaryConcerns: [...NON_BASELINE_CONCERNS] });
      setBulkSelectAt(Date.now());
    }
  }

  return (
    <StepShell
      stepId="concerns"
      overrides={overrides}
      headline="What do you want to understand about your health?"
      subtitle="Pick up to 6, or select all if you want the full baseline picture."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {NON_BASELINE_CONCERNS.map((c, i) => {
          const Icon = CONCERN_ICON[c];
          const isSelected = selected.includes(c);
          // Track 5.Y3: when the bulk-select fires, use animation-delay
          // to stagger the per-card transition (50ms × index, 200ms
          // duration). The wrapper key includes bulkSelectAt so the
          // wrapper remounts + replays the animation. On individual
          // toggles, no remount → no stagger.
          const animateBulk = isSelected && bulkSelectAt > 0 && allSelected;
          return (
            <div
              key={`${c}-${animateBulk ? bulkSelectAt : 'static'}`}
              style={animateBulk ? { animation: `concerns-bulk-fade-in 200ms ease-out ${i * 50}ms both` } : undefined}
            >
              <OptionCard
                label={applyOptionOverrideSerialized(overrides, 'concerns', c, CONCERN_LABEL[c].label)}
                description={CONCERN_LABEL[c].description}
                selected={isSelected}
                multiSelect
                icon={<Icon size={22} strokeWidth={2} aria-hidden />}
                onClick={() => toggle(c)}
                disabled={!isSelected && selected.length >= CONCERNS_MAX && !allSelected}
              />
            </div>
          );
        })}
      </div>

      {/* Track 5.Y3: "Show me everything" — single-path comprehensive
          funnel CTA. "Just a baseline" + OR-JUST-EXPLORE divider removed
          this PR.
          Track 5.Z3 (2026-05-12): navy theme (was brand-orange).
          Increased top spacing from mt-4 → mt-6 so the card reads as
          a distinct affordance, not a 13th concern. */}
      <button
        type="button"
        onClick={selectAll}
        aria-pressed={allSelected}
        className={[
          'mt-6 w-full text-left rounded-xl border-2 px-5 py-4 transition-all duration-200',
          'flex items-start gap-3',
          allSelected
            ? 'border-[#0f1e3d] bg-slate-50 shadow-sm'
            : 'border-slate-200 bg-white hover:border-slate-400 hover:bg-slate-50 hover:-translate-y-0.5 hover:shadow-md',
        ].join(' ')}
      >
        <div
          className="shrink-0 h-10 w-10 rounded-full flex items-center justify-center"
          style={{ backgroundColor: allSelected ? '#0f1e3d' : 'rgba(15,30,61,0.08)', color: allSelected ? '#fff' : '#0f1e3d' }}
          aria-hidden
        >
          <Sparkles size={20} strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#0f2534]">
            Show me everything
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {allSelected
              ? 'Includes all 12 — click any above to deselect.'
              : 'Selects all 12 concerns at once.'}
          </div>
        </div>
      </button>

      {/* Stagger animation keyframes — local to this step. */}
      <style jsx>{`
        @keyframes concerns-bulk-fade-in {
          0% { opacity: 0.55; transform: translateY(-4px) scale(0.985); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </StepShell>
  );
}

// Phase 1 dimension steps — energy / focus / memory / sleep_band.
// Each is a single-select card list (no slider). Five band cards on
// energy, four on focus, four on memory, six on sleep_band per the
// operator-locked structure.

export function EnergyStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  return (
    <StepShell
      stepId="energy"
      overrides={overrides}
      headline="How's your energy?"
      subtitle="Pick the one that's most like you most days."
    >
      <div className="grid grid-cols-1 gap-2">
        {ENERGY_PATTERNS.map((e) => (
          <OptionCard
            key={e}
            label={applyOptionOverrideSerialized(overrides, 'energy', e, ENERGY_LABEL[e].label)}
            description={ENERGY_LABEL[e].description}
            selected={profile.energyPattern === e}
            onClick={() => onChange({ energyPattern: e })}
          />
        ))}
      </div>
    </StepShell>
  );
}

export function FocusStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  return (
    <StepShell
      stepId="focus"
      overrides={overrides}
      headline="How's your focus?"
      subtitle="Mental clarity through your typical workday."
    >
      <div className="grid grid-cols-1 gap-2">
        {COGNITIVE_STATES.map((c) => (
          <OptionCard
            key={c}
            label={applyOptionOverrideSerialized(overrides, 'focus', c, COGNITIVE_LABEL[c].label)}
            description={COGNITIVE_LABEL[c].description}
            selected={profile.cognitiveState === c}
            onClick={() => onChange({ cognitiveState: c })}
          />
        ))}
      </div>
    </StepShell>
  );
}

export function MemoryStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  return (
    <StepShell
      stepId="memory"
      overrides={overrides}
      headline="How's your memory?"
      subtitle="Names, tasks, conversations — over the past few months."
    >
      <div className="grid grid-cols-1 gap-2">
        {MEMORY_STATES.map((m) => (
          <OptionCard
            key={m}
            label={applyOptionOverrideSerialized(overrides, 'memory', m, MEMORY_LABEL[m].label)}
            description={MEMORY_LABEL[m].description}
            selected={profile.memoryState === m}
            onClick={() => onChange({ memoryState: m })}
          />
        ))}
      </div>
    </StepShell>
  );
}

export function SleepBandStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  return (
    <StepShell
      stepId="sleep_band"
      overrides={overrides}
      headline="How much sleep do you average?"
      subtitle="A typical weeknight, not your best or worst."
    >
      <div className="grid grid-cols-1 gap-2">
        {SLEEP_HOURS_BANDS.map((b) => (
          <OptionCard
            key={b}
            label={applyOptionOverrideSerialized(overrides, 'sleep_band', b, SLEEP_BAND_LABEL[b].label)}
            description={SLEEP_BAND_LABEL[b].description}
            selected={profile.sleepHoursBand === b}
            onClick={() => onChange({ sleepHoursBand: b })}
          />
        ))}
      </div>
    </StepShell>
  );
}

export function LifestyleStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  return (
    <StepShell
      stepId="lifestyle"
      overrides={overrides}
      headline="A bit about how you live."
      subtitle="Two more lifestyle questions. These shape which markers we surface."
    >
      <div className="space-y-6">
        <div>
          <p className="text-sm font-semibold text-[#0f2534] mb-2">How active are you typically?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ACTIVITY_LEVELS.map((a) => (
              <OptionCard
                key={a}
                label={applyOptionOverrideSerialized(overrides, 'lifestyle', a, ACTIVITY_LABEL[a].label)}
                description={ACTIVITY_LABEL[a].description}
                selected={profile.activityLevel === a}
                onClick={() => onChange({ activityLevel: a })}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-[#0f2534] mb-2">What does your diet look like?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {DIET_PATTERNS.map((d) => (
              <OptionCard
                key={d}
                label={applyOptionOverrideSerialized(overrides, 'lifestyle', d, DIET_LABEL[d].label)}
                description={DIET_LABEL[d].description}
                selected={profile.dietPattern === d}
                onClick={() => onChange({ dietPattern: d })}
              />
            ))}
          </div>
        </div>
      </div>
    </StepShell>
  );
}

export function ConditionsStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  const selected = profile.diagnosedConditions ?? [];
  function toggle(c: DiagnosedCondition) {
    if (c === 'none') {
      onChange({ diagnosedConditions: selected.includes('none') ? [] : ['none'] });
      return;
    }
    const without = selected.filter((x) => x !== 'none' && x !== c);
    if (selected.includes(c)) {
      onChange({ diagnosedConditions: without });
    } else {
      onChange({ diagnosedConditions: [...without, c] });
    }
  }
  return (
    <StepShell
      stepId="conditions"
      overrides={overrides}
      headline="Any of these apply to you?"
      subtitle="Pick any that fit. It's fine if none do."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {DIAGNOSED_CONDITIONS.map((c) => {
          const Icon = CONDITION_ICON[c];
          return (
            <OptionCard
              key={c}
              label={applyOptionOverrideSerialized(overrides, 'conditions', c, CONDITION_LABEL[c])}
              selected={selected.includes(c)}
              multiSelect
              icon={<Icon size={22} strokeWidth={2} aria-hidden />}
              onClick={() => toggle(c)}
            />
          );
        })}
      </div>
    </StepShell>
  );
}

export function FamilyHistoryStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  const selected = profile.familyHistory ?? [];
  function toggle(c: FamilyHistoryCategory) {
    const exclusive: FamilyHistoryCategory[] = ['none', 'unknown'];
    if (exclusive.includes(c)) {
      onChange({ familyHistory: selected.includes(c) ? [] : [c] });
      return;
    }
    const without = selected.filter((x) => !exclusive.includes(x) && x !== c);
    if (selected.includes(c)) {
      onChange({ familyHistory: without });
    } else {
      onChange({ familyHistory: [...without, c] });
    }
  }
  return (
    <StepShell
      stepId="family_history"
      overrides={overrides}
      headline="What runs in your family?"
      subtitle="Tell us what you know — or skip if you're not sure."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {FAMILY_HISTORY_CATEGORIES.map((f) => {
          const Icon = FAMILY_ICON[f];
          return (
            <OptionCard
              key={f}
              label={applyOptionOverrideSerialized(overrides, 'family_history', f, FAMILY_LABEL[f])}
              selected={selected.includes(f)}
              multiSelect
              icon={<Icon size={22} strokeWidth={2} aria-hidden />}
              onClick={() => toggle(f)}
            />
          );
        })}
      </div>
    </StepShell>
  );
}

export function GoalStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  const selected = profile.primaryGoals ?? [];
  function toggle(g: PrimaryGoal) {
    if (selected.includes(g)) {
      onChange({ primaryGoals: selected.filter((x) => x !== g) });
    } else {
      onChange({ primaryGoals: [...selected, g] });
    }
  }
  return (
    <StepShell
      stepId="goal"
      overrides={overrides}
      headline="What brought you here?"
      subtitle="Pick any that apply. We'll tune recommendations to your selection."
    >
      <div className="grid grid-cols-1 gap-2">
        {PRIMARY_GOALS.map((g) => {
          const Icon = GOAL_ICON[g];
          return (
            <OptionCard
              key={g}
              label={applyOptionOverrideSerialized(overrides, 'goal', g, GOAL_LABEL[g].label)}
              description={GOAL_LABEL[g].description}
              selected={selected.includes(g)}
              multiSelect
              icon={<Icon size={22} strokeWidth={2} aria-hidden />}
              onClick={() => toggle(g)}
            />
          );
        })}
      </div>
    </StepShell>
  );
}

/**
 * Track 3.10 (2026-05-14) — merged 'name' + 'email_phone' steps into one.
 * Captures firstName + lastName + email + consent (the consent checkbox is
 * still rendered separately by StartFlowClient below this step's body).
 * step_id stays 'email_phone' for stability of operator-authored
 * quiz_step_overrides rows. NameStep export removed; if a future revival
 * is needed, restore from git history.
 */
export function EmailPhoneStep({ lead, onLeadChange, overrides = NO_OVERRIDES }: { lead: LeadForm; onLeadChange: LeadChange; overrides?: SerializedQuizOverrides }) {
  return (
    <StepShell
      stepId="email_phone"
      overrides={overrides}
      headline="Let's set up your account."
      subtitle="We'll send your results here. Phone + text opt-in are collected on the next step."
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="First name" required value={lead.firstName} onChange={(v) => onLeadChange({ firstName: v })} autoComplete="given-name" />
          <FormField label="Last name" required value={lead.lastName} onChange={(v) => onLeadChange({ lastName: v })} autoComplete="family-name" />
        </div>
        <FormField label="Email" required type="email" value={lead.email} onChange={(v) => onLeadChange({ email: v })} autoComplete="email" inputMode="email" placeholder="you@example.com" />
        <p className="text-xs text-slate-500">We use your name on your lab requisition; email is how we send your results.</p>
      </div>
    </StepShell>
  );
}

// Track 5.Y2-start-quiz-polish: phone + TCPA opt-in moved to /start
// from /checkout. Reverses §6.2 2026-04-30 (see HEARTBEAT §6.2
// 2026-05-12 entry). The TCPA opt-in is the substantive discipline
// — defaults UNCHECKED, includes Reply-STOP language, links to
// /legal/sms-terms — that wasn't present in the pre-2026-04-30
// /start phone collection.
function formatPhoneDisplay(digits: string): string {
  const d = digits.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function DobSexZipStep({ lead, onLeadChange, overrides = NO_OVERRIDES }: { lead: LeadForm; onLeadChange: LeadChange; overrides?: SerializedQuizOverrides }) {
  // Track 5.Y3-start-quiz-bugfix-polish (2026-05-12): phoneShowError
  // logic fix. Pre-fix: `phone.length > 0 && !valid && digits >= 10` was
  // unsatisfiable (digits === 10 implied valid). Post-fix: error renders
  // when the user has typed any digits but the count is short of 10 —
  // i.e. they're partially through and haven't completed it. Also fires
  // on full-length non-digit-only entry (defensive). Continue button is
  // separately gated in StartFlowClient.canAdvanceFromStep.
  const phoneDigits = lead.phone.replace(/\D/g, '');
  const phoneValid = phoneDigits.length === 10;
  const phoneTouched = lead.phone.length > 0;
  const phoneShowError = phoneTouched && !phoneValid;

  return (
    <StepShell
      stepId="dob_sex_zip"
      overrides={overrides}
      headline="A few more details."
      subtitle="We use these to find a LabCorp near you and personalize your reference ranges."
    >
      {/* Track 5.Y3 relayout: DOB + ZIP in 2-col (matched-width inputs),
          Sex full-width (2 buttons get room to breathe), Phone + TCPA
          full-width below. Fixes the visual rhythm break from the prior
          3-col grid where Sex's 2-button sub-grid was squeezed into a
          1/3-width parent column and the column heights diverged. */}
      <div className="space-y-5">
        {/* Row 1 — DOB | ZIP */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <FormField
              label="Date of birth"
              required
              type="date"
              value={lead.dateOfBirth}
              onChange={(v) => onLeadChange({ dateOfBirth: v })}
              autoComplete="bday"
              hint="Used to adjust reference ranges for age."
            />
          </div>
          <div>
            <FormField
              label="ZIP code"
              required
              value={lead.zipCode}
              onChange={(v) => onLeadChange({ zipCode: v.replace(/\D/g, '').slice(0, 5) })}
              autoComplete="postal-code"
              inputMode="numeric"
              placeholder="12345"
              hint="We use this to find a LabCorp near you."
            />
          </div>
        </div>

        {/* Row 2 — Sex at birth (full width, 2 buttons share the row) */}
        <div>
          <span className="block text-xs font-semibold tracking-wide text-slate-700 mb-1.5">
            Sex at birth <span className="text-amber-600 ml-0.5">*</span>
          </span>
          <div className="grid grid-cols-2 gap-2">
            <OptionCard label="Female" selected={lead.sexAtBirth === 'female'} onClick={() => onLeadChange({ sexAtBirth: 'female' })} />
            <OptionCard label="Male" selected={lead.sexAtBirth === 'male'} onClick={() => onLeadChange({ sexAtBirth: 'male' })} />
          </div>
          <p className="mt-1.5 text-xs text-slate-500">Some markers have sex-specific ranges.</p>
        </div>

        {/* Row 3 — Phone + TCPA opt-in (full width) */}
        <div>
          <FormField
            label="Phone number"
            required
            type="tel"
            value={formatPhoneDisplay(lead.phone)}
            onChange={(v) => onLeadChange({ phone: v.replace(/\D/g, '').slice(0, 10) })}
            autoComplete="tel-national"
            inputMode="tel"
            placeholder="(555) 555-5555"
            error={phoneShowError ? 'Please enter a 10-digit US phone number.' : null}
          />

          {/* TCPA-compliant opt-in — defaults UNCHECKED.
              Track 5.Z3 (2026-05-12): checkbox checked-state flipped
              from brand-orange to navy. */}
          <label className="mt-3 flex items-start gap-3 cursor-pointer group">
            <span
              className={[
                'mt-0.5 shrink-0 h-5 w-5 rounded-md border-2 flex items-center justify-center transition-colors',
                lead.smsConsent
                  ? 'bg-[#0f1e3d] border-[#0f1e3d]'
                  : 'bg-white border-slate-300 group-hover:border-slate-400',
              ].join(' ')}
              aria-hidden
            >
              {lead.smsConsent && (
                <Check size={14} strokeWidth={3.5} className="text-white" />
              )}
            </span>
            <input
              type="checkbox"
              checked={lead.smsConsent}
              onChange={(e) => onLeadChange({ smsConsent: e.target.checked })}
              className="sr-only"
              aria-label="Agree to receive SMS notifications about service and results"
            />
            <span className="text-xs text-slate-600 leading-relaxed">
              I agree to receive notifications about my service and results, including blood draw reminders and fasting reminders. Message and data rates may apply. Reply STOP to opt out at any time.
            </span>
          </label>
          <p className="mt-2 text-xs text-slate-400 pl-8">
            <LegalLink slug="sms-terms" className="underline hover:text-slate-600">
              See full notification terms
            </LegalLink>
          </p>
        </div>
      </div>
    </StepShell>
  );
}

export function MenstrualStep({ profile, onChange, overrides = NO_OVERRIDES }: { profile: HealthProfileInput; onChange: ProfileChange; overrides?: SerializedQuizOverrides }) {
  return (
    <StepShell
      stepId="menstrual"
      overrides={overrides}
      eyebrow="Cycle context"
      headline="A few cycle questions."
      subtitle="Some hormone ranges shift with cycle phase. You can skip any of these."
    >
      <div className="space-y-6">
        <div>
          <p className="text-sm font-semibold text-[#0f2534] mb-2">Cycle status</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {MENSTRUAL_STATUSES.map((m) => (
              <OptionCard key={m} label={MENSTRUAL_LABEL[m]} selected={profile.menstrualStatus === m} onClick={() => onChange({ menstrualStatus: m })} />
            ))}
          </div>
        </div>
        {(profile.menstrualStatus === 'regular' || profile.menstrualStatus === 'irregular') && (
          <div>
            <p className="text-sm font-semibold text-[#0f2534] mb-2">Where are you in your cycle right now?</p>
            <div className="grid grid-cols-1 gap-2">
              {CYCLE_PHASES.map((c) => (
                <OptionCard key={c} label={CYCLE_LABEL[c]} selected={profile.cyclePhase === c} onClick={() => onChange({ cyclePhase: c })} />
              ))}
            </div>
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-[#0f2534] mb-2">Birth control (optional)</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {BIRTH_CONTROL_TYPES.map((b) => (
              <OptionCard key={b} label={BIRTH_CONTROL_LABEL[b]} selected={profile.birthControlType === b} onClick={() => onChange({ birthControlType: b })} />
            ))}
          </div>
        </div>
      </div>
    </StepShell>
  );
}

// BiometricsStep was removed from the active flow in Step 17.3.5
// to reduce funnel friction. Component preserved for future BMI-
// calculator placement at src/app/start/_deferred/BiometricsStep.tsx.
// Schema (height_cm / weight_kg / biometrics_unit_pref) and
// /api/health-profile/upsert continue to accept these fields so
// the dashboard Account page (or a future conditional step) can
// write them with zero backend changes.
